import { Formik, FormikProps, useFormik } from 'formik';
import { useWorkplaceUser } from 'module/documents/utils';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	createContext,
	ReactNode,
	useContext,
	useState,
	Dispatch,
	SetStateAction,
} from 'react';
import { User } from '../../types';

interface NewEmployeeType {
	firstName: string;
	middleName: string;
	lastName: string;
	email: string;
}

/**
 * Shared app shell state previously owned by the old DocumentModal onboarding flow.
 * Formik / employee-form fields are retained as inert stubs so existing consumers
 * of selectedEmployee / date range / scheduleView keep compiling; the Formik UI is gone.
 */
interface DocumentContextType {
	formik: FormikProps<any>;
	resetForm: (newValues: any) => void;
	isEmployer: boolean;
	selectedEmployee: User;
	setSelectedEmployee: React.Dispatch<React.SetStateAction<User>>;
	loadedFormIds: Record<string, any>;
	recordFormSubmission: (formId: string) => void;
	setLoadedFormIds: React.Dispatch<React.SetStateAction<Record<string, any>>>;
	newEmployee: NewEmployeeType;
	setNewEmployee: React.Dispatch<React.SetStateAction<NewEmployeeType>>;
	newEmployeeCreated: boolean;
	setNewEmployeeCreated: React.Dispatch<React.SetStateAction<boolean>>;
	handleModalClose: () => void;
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	handleOpen: () => void;
	startDate: string;
	setStartDate: Dispatch<SetStateAction<string>>;
	endDate: string;
	setEndDate: Dispatch<SetStateAction<string>>;
	businessWeekStart: string;
	setBusinessWeekStart: Dispatch<SetStateAction<string>>;
	scheduleView: string;
	setScheduleView: Dispatch<SetStateAction<string>>;
	activeTabIndex: number;
	setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
	submittedForms: Record<string, boolean>;
	setSubmittedForms: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
	employeeState: string;
	setEmployeeState: React.Dispatch<React.SetStateAction<string>>;
	translatorSignatureFile: File | null;
	setTranslatorSignatureFile: React.Dispatch<React.SetStateAction<File | null>>;
	editSupplementB: boolean;
	setEditSupplementB: React.Dispatch<React.SetStateAction<boolean>>;
	resetDocumentContext: (newState?: string) => void;
	lastLoadedEmployeeId: number | null;
	setLastLoadedEmployeeId: Dispatch<SetStateAction<number | null>>;
}

const DocumentContext = createContext<DocumentContextType | null>(null);

export const useDocumentContext = (): DocumentContextType => {
	const context = useContext(DocumentContext);
	if (!context) {
		throw new Error('useDocumentContext must be used within a DocumentProvider');
	}
	return context;
};

interface DocumentProviderProps {
	children: ReactNode;
	defaultUser: User;
	newEmployeeState: [NewEmployeeType, Dispatch<SetStateAction<NewEmployeeType>>];
}

export const DocumentProvider = ({
	children,
	defaultUser,
	newEmployeeState,
}: DocumentProviderProps) => {
	const [selectedEmployee, setSelectedEmployee] = useState<User>(defaultUser);
	const [loadedFormIds, setLoadedFormIds] = useState<Record<string, any>>({});
	const [newEmployee, setNewEmployee] = newEmployeeState;
	useWorkplacesContext();
	useWorkplaceUser();
	const [open, setOpen] = useState(false);
	const handleOpen = () => setOpen(true);
	const handleModalClose = () => setOpen(false);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [businessWeekStart, setBusinessWeekStart] = useState('');
	const [scheduleView, setScheduleView] = useState('scheduler');
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const [translatorSignatureFile, setTranslatorSignatureFile] = useState<File | null>(null);
	const [lastLoadedEmployeeId, setLastLoadedEmployeeId] = useState<number | null>(null);
	const [submittedForms, setSubmittedForms] = useState<Record<string, boolean>>({});
	const [newEmployeeCreated, setNewEmployeeCreated] = useState(false);
	const [editSupplementB, setEditSupplementB] = useState(false);
	const [employeeState, setEmployeeState] = useState('new_employee');
	const isEmployer = false;

	const initialValues = {};

	const formik = useFormik({
		initialValues,
		validate: (_values) => ({}),
		onSubmit: () => undefined,
	});

	const resetForm = (newValues: any) =>
		formik.resetForm({ values: { ...initialValues, ...newValues } });

	const recordFormSubmission = (formId: string) => {
		setLoadedFormIds((prev) => ({ ...prev, [formId]: true }));
	};

	const resetDocumentContext = (newState?: string) => {
		if (!newState) {
			setEmployeeState('new_employee');
		}
		setTranslatorSignatureFile(null);
		formik.resetForm();
		setNewEmployee({
			firstName: '',
			middleName: '',
			lastName: '',
			email: '',
		});
		setSubmittedForms({});
	};

	return (
		<Formik initialValues={initialValues} onSubmit={() => undefined} enableReinitialize={true}>
			<DocumentContext.Provider
				value={{
					formik,
					resetForm,
					isEmployer,
					selectedEmployee,
					setSelectedEmployee,
					loadedFormIds,
					setLoadedFormIds,
					recordFormSubmission,
					newEmployee,
					setNewEmployee,
					newEmployeeCreated,
					setNewEmployeeCreated,
					handleModalClose,
					open,
					setOpen,
					handleOpen,
					startDate,
					setStartDate,
					endDate,
					setEndDate,
					businessWeekStart,
					setBusinessWeekStart,
					scheduleView,
					setScheduleView,
					activeTabIndex,
					setActiveTabIndex,
					submittedForms,
					setSubmittedForms,
					employeeState,
					setEmployeeState,
					translatorSignatureFile,
					setTranslatorSignatureFile,
					editSupplementB,
					setEditSupplementB,
					resetDocumentContext,
					lastLoadedEmployeeId,
					setLastLoadedEmployeeId,
				}}
			>
				{children}
			</DocumentContext.Provider>
		</Formik>
	);
};
